import { Log } from '@apparel/core/log';
import { uuid } from '@lib/random';
import { isProduction } from '@lib/utils';
import { castArray } from 'lodash';
import { GetterSetter } from '../../objects/lib/getterSetter';

export interface SingletoneClass<T> {
    new (...args: any[]): T;

    __UUID__?: string;
    __BIND__?: string;
    __NAME__?: string;
    __instance?: SingletoneClass<T>;

    getInstance: (...args: any[]) => T;
}

export class Application extends GetterSetter {
    static __instance: Application;

    static getInstance(): Application {
        if (!Application.__instance) {
            Application.__instance = new Application();
        }

        return Application.__instance;
    }

    __SERVICES__: { [key: string]: any } = {};

    __unknownsCount = 0;

    logger = Log.channel('Application');

    /**
     *
     * @param $class
     * @param args
     * @returns {*}
     */
    make<T>($class: SingletoneClass<T>, ...args: any[]): T {
        if (!$class.hasOwnProperty('__UUID__') || !$class.__UUID__) {
            $class.__UUID__ = uuid();
        }

        if (!$class.hasOwnProperty('__NAME__') || !$class.__NAME__) {
            $class.__NAME__ = $class.name || ($class.constructor || {}).name || `Unknown ${this.__unknownsCount++}`;
        }

        if (typeof $class.getInstance !== 'function') {
            $class.getInstance = (...argList: any[]): any => {
                if (!$class.__instance) {
                    $class.__instance = new $class(...argList) as any;
                }

                return $class.__instance;
            };
        }

        if ($class.__BIND__) {
            if (!this.has($class.__BIND__)) {
                this.set($class.__BIND__, $class.getInstance(...args));
            }
        }

        return $class.getInstance(...args);
    }

    /**
     * Bind instances
     * @param key
     * @param value
     * @param args
     * @returns {Application}
     */
    bind<B = any>(key: string, value: any, ...args: any[]): B {
        if (!this.hasService(key)) {
            const service = this.make(value, ...args);

            this.setService(key, service);

            return service as any;
        }

        return this.getService(key);
    }

    /**
     *
     * @param services
     */
    services(services: { [key: string]: any }) {
        if (!services) {
            services = [];
        }

        if (typeof services && !Array.isArray(services)) {
            services = Array.from(Object.entries(services));
        }

        this.__SERVICES__ = services;
        return this;
    }

    _serviceKey(key: string): string {
        return `services.${key}`;
    }

    hasService(key: string): boolean {
        return this.has(this._serviceKey(key));
    }

    getService<T = any>(key: string): T {
        return this.get(this._serviceKey(key));
    }

    setService(key: string, value: any) {
        return this.set(this._serviceKey(key), value);
    }

    /**
     * Application Boot
     */
    async boot() {
        const boot: string[] = [];

        console.groupCollapsed('Application::boot');

        // Create binds
        this.__SERVICES__.forEach(([key, values]) => {
            values = castArray(values);

            this.logger.debug('Application::bind(%s)', key, isProduction() ? { values } : []);
            // @ts-ignore
            const service = this.bind(key, ...values);

            if (typeof service.boot === 'function') {
                boot.push(key);
            }
        });

        await Promise.all(boot.map((service) => this.getService(service).boot()));

        console.groupEnd();
    }
}
